import React, { useState } from 'react';
import { User } from 'lucide-react';
import Button from '@components/common/Button';
import MultiRoleAssignmentModal from '@components/common/MultiRoleAssignmentModal';
import { formatPersonName } from '@utils/stringUtils';
import type { User as AppUser } from '@app-types';
import styles from './ContractFormModal.module.css';

interface ContractRolePickerProps {
  roleType: 'owner' | 'manager';
  selectedIds: number[];
  users: AppUser[];
  contractNumber: string;
  supplierName?: string;
  disabled?: boolean;
  onChange: (ids: number[]) => void;
}

const ROLE_LABEL: Record<'owner' | 'manager', string> = {
  owner: 'Contract Owner',
  manager: 'Contract Manager'
};

const ContractRolePicker: React.FC<ContractRolePickerProps> = ({
  roleType,
  selectedIds,
  users,
  contractNumber,
  supplierName,
  disabled,
  onChange
}) => {
  const [showModal, setShowModal] = useState(false);
  const label = ROLE_LABEL[roleType];
  const selectedUsers = users.filter((user) => selectedIds.includes(user.id));
  const displayNames = selectedUsers.map((user) => formatPersonName(user)).join(', ');
  const isAssigned = selectedIds.length > 0;

  return (
    <>
      <div className={styles.roleSelectionSection}>
        <span className={styles.formLabel}>{label}</span>
        {isAssigned ? (
          <div className={styles.selectedSupplierDisplay}>
            <div className={styles.supplierInfo}>
              <User size={16} />
              <div>
                <div className={styles.supplierName}>{displayNames || `${selectedIds.length} selected`}</div>
              </div>
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setShowModal(true)}
              disabled={disabled}
              dataTestId={`contract-change-${roleType}-btn`}
            >
              Change
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            variant="secondary"
            onClick={() => setShowModal(true)}
            disabled={disabled}
            dataTestId={`contract-select-${roleType}-btn`}
          >
            <User size={16} />
            Select {label}
          </Button>
        )}
      </div>

      {showModal && (
        <MultiRoleAssignmentModal
          contractNumber={contractNumber}
          supplierName={supplierName}
          roleType={roleType}
          currentAssigneeIds={selectedIds}
          onClose={() => setShowModal(false)}
          onAssigned={async (ids) => {
            onChange(ids);
          }}
          dataTestId={`contract-form-${roleType}`}
        />
      )}
    </>
  );
};

export default ContractRolePicker;
